import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Category } from "Types/Navbar";
import { encodeParam } from "Utilities/urlEncoder";
import { useSearchAnalytics } from "Context/SearchAnalyticsContext";

interface CategoryDropdownProps {
  style: React.CSSProperties;
  categories: Category[];
  hoveredCategory: string | null;
  onHoverCategory: (category: string | null) => void;
  onMouseLeave: () => void;
}

const MAX_VISIBLE_ITEMS = 10;
const ITEM_HEIGHT = 36;

const CategoryDropdown: React.FC<CategoryDropdownProps> = ({
  style,
  categories,
  hoveredCategory,
  onHoverCategory,
  onMouseLeave,
}) => {
  const navigate = useNavigate();
  const { queueAnalytics, resolveAnalytics } = useSearchAnalytics();
  const searchTimeoutsRef = useRef<Record<string, NodeJS.Timeout>>({});
  const lastLoggedRef = useRef<Record<string, string>>({});

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.values(searchTimeoutsRef.current).forEach(clearTimeout);
    };
  }, []);

  const [search, setSearch] = useState("");
  const [subSearch, setSubSearch] = useState("");

  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const isInSubPanel = useRef(false);

  const activeCategories = useMemo(() => {
    return categories
      .filter((c) => c.isActive === "1")
      .filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase())
      );
  }, [categories, search]);

  const filteredSubCategories = useMemo(() => {
    if (!hoveredCategory) return [];

    const subs =
      activeCategories.find((c) => c.name === hoveredCategory)?.subs || [];

    return subs.filter((sub) =>
      sub.name.toLowerCase().includes(subSearch.toLowerCase())
    );
  }, [hoveredCategory, activeCategories, subSearch]);

  useEffect(() => {
    if (!isInSubPanel.current) {
      setSubSearch("");
    }
  }, [hoveredCategory]);

  const handleCategoryClick = (categoryCode: string) => {
    const categoryName = activeCategories.find((c) => c.code === categoryCode)?.name ?? categoryCode;
    queueAnalytics("CATEGORY", categoryName);
    navigate(`/books-listing?category=${encodeParam(categoryCode)}`);
  };

  // 🔹 Keyboard support
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!activeCategories.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < activeCategories.length - 1 ? prev + 1 : prev
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
    }

    if (e.key === "Enter") {
      handleCategoryClick(activeCategories[activeIndex].code);
    }

    if (e.key === "Escape") {
      onMouseLeave();
    }
  };

  return (
    <>
        <div
          className="absolute top-full mt-1 w-[50vw] xl:w-[25vw] bg-vintageBg bg-opacity-95 rounded-xl z-50 shadow-xl grid grid-cols-2 transition-all duration-150"
          style={style}
          onMouseLeave={() => {
            onMouseLeave();
            onHoverCategory(null);
          }}
        >
          {/* LEFT PANEL — CATEGORY LIST */}
          <div className="w-full py-4 px-2 border-r">
            <div className="relative w-full mb-3">
              <input
                type="text"
                placeholder="Search categories..."
                value={search}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearch(val);
                  setActiveIndex(0);
                  const key = "category";
                  if (searchTimeoutsRef.current[key]) {
                    clearTimeout(searchTimeoutsRef.current[key]);
                  }
                  if (!val.trim()) {
                    lastLoggedRef.current[key] = "";
                  } else {
                    searchTimeoutsRef.current[key] = setTimeout(() => {
                      const matched = categories
                        .filter((c) => c.isActive === "1")
                        .filter((c) => c.name.toLowerCase().includes(val.toLowerCase()));
                      if (matched.length === 0) {
                        const trimmed = val.trim();
                        const lastLogged = lastLoggedRef.current[key] || "";
                        const searchType = "CATEGORY";
                        if (lastLogged && trimmed.startsWith(lastLogged)) {
                          try {
                            const logsKey = "sa_no_result_logs";
                            const logs = JSON.parse(localStorage.getItem(logsKey) || "[]");
                            const lastIndex = [...logs].reverse().findIndex(
                              (log: any) => log.searchType === searchType && log.searchData === lastLogged
                            );
                            if (lastIndex !== -1) {
                              const targetIndex = logs.length - 1 - lastIndex;
                              logs.splice(targetIndex, 1);
                              localStorage.setItem(logsKey, JSON.stringify(logs));
                            }
                          } catch (err) {
                            console.error("Error updating logs:", err);
                          }
                        } else if (lastLogged && lastLogged.startsWith(trimmed)) {
                          return;
                        }
                        queueAnalytics(searchType, trimmed);
                        resolveAnalytics(false);
                        lastLoggedRef.current[key] = trimmed;
                      }
                    }, 800);
                  }
                }}
                onKeyDown={handleKeyDown}
                className="w-full pr-8 px-2 py-1 text-sm border rounded-md focus:outline-none"
              />
              <div
                className="absolute right-2 top-0 h-full flex items-center text-gray-700 text-opacity-50 cursor-pointer hover:text-vintageText transition-colors"
                tabIndex={0}
                role="button"
                aria-label="Search Categories"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="lucide lucide-search"
                >
                  <path d="m21 21-4.34-4.34" />
                  <circle cx="11" cy="11" r="8" />
                </svg>
              </div>
            </div>
            {activeCategories.length === 0 ? (
              <p className="text-sm text-gray-500">No results found</p>
            ) : (
              <ul
                ref={listRef}
                className="space-y-1 overflow-y-auto hide-scrollbar"
                style={{ maxHeight: `${ITEM_HEIGHT * MAX_VISIBLE_ITEMS}px` }}
              >
                {activeCategories.map((category) => (
                  <li
                    key={category.code}
                    onMouseEnter={() => {
                      if (!isInSubPanel.current) onHoverCategory(category.name);
                    }}
                    onClick={() => handleCategoryClick(category.code)}
                    className={`py-2 px-2 cursor-pointer text-sm lg:text-base text-gray-800 font-gilroyRegular font-semibold tracking-wider flex justify-between items-center ${
                      hoveredCategory === category.name
                        ? "bg-vintageText bg-opacity-10"
                        : "hover:bg-vintageText hover:bg-opacity-5"
                    }`}
                  >
                    <span className="truncate" title={category.name}>
                      {category.name}
                    </span>
                    {(category.subs?.length ?? 0) > 0 && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="ml-1 shrink-0 text-gray-700"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* RIGHT PANEL — SUBCATEGORIES */}
          <div
            className="w-full py-4 px-2"
            onMouseEnter={() => { isInSubPanel.current = true; }}
            onMouseLeave={() => { isInSubPanel.current = false; }}
          >
            {hoveredCategory ? (() => {
              const hoveredCat = activeCategories.find((c) => c.name === hoveredCategory);
              return (
                <>
                  <div className="relative w-full mb-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      placeholder="Search subcategories..."
                      value={subSearch}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSubSearch(val);
                        const key = "subCategory";
                        if (searchTimeoutsRef.current[key]) {
                          clearTimeout(searchTimeoutsRef.current[key]);
                        }
                        if (!val.trim()) {
                          lastLoggedRef.current[key] = "";
                        } else {
                          searchTimeoutsRef.current[key] = setTimeout(() => {
                            const matched = (hoveredCat?.subs || []).filter(
                              (sub) => sub.name.toLowerCase().includes(val.toLowerCase())
                            );
                            if (matched.length === 0) {
                              const trimmed = val.trim();
                              const lastLogged = lastLoggedRef.current[key] || "";
                              const searchType = "SUB_CATEGORY";
                              if (lastLogged && trimmed.startsWith(lastLogged)) {
                                try {
                                  const logsKey = "sa_no_result_logs";
                                  const logs = JSON.parse(localStorage.getItem(logsKey) || "[]");
                                  const lastIndex = [...logs].reverse().findIndex(
                                    (log: any) => log.searchType === searchType && log.searchData === lastLogged
                                  );
                                  if (lastIndex !== -1) {
                                    const targetIndex = logs.length - 1 - lastIndex;
                                    logs.splice(targetIndex, 1);
                                    localStorage.setItem(logsKey, JSON.stringify(logs));
                                  }
                                } catch (err) {
                                  console.error("Error updating logs:", err);
                                }
                              } else if (lastLogged && lastLogged.startsWith(trimmed)) {
                                return;
                              }
                              queueAnalytics(searchType, trimmed);
                              resolveAnalytics(false);
                              lastLoggedRef.current[key] = trimmed;
                            }
                          }, 800);
                        }
                      }}
                      className="w-full pr-8 px-2 py-1 text-sm border rounded-md focus:outline-none"
                    />
                    <div
                      className="absolute right-2 top-0 h-full flex items-center text-gray-700 text-opacity-50 cursor-pointer hover:text-vintageText transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label="Search Subcategories"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-search"
                      >
                        <path d="m21 21-4.34-4.34" />
                        <circle cx="11" cy="11" r="8" />
                      </svg>
                    </div>
                  </div>
                  <div
                    className="overflow-y-auto hide-scrollbar space-y-1"
                    style={{ maxHeight: `${ITEM_HEIGHT * (MAX_VISIBLE_ITEMS - 1.5)}px` }}
                  >
                    {filteredSubCategories.length === 0 ? (
                      <p className="text-sm text-gray-500">No results found</p>
                    ) : (
                      filteredSubCategories.map((sub) => (
                        <div
                          key={sub.code}
                          onClick={() => {
                            const subCatValue = sub.code === "none" || sub.code === "None" ? "None" : `${hoveredCat?.code}_${sub.code}`;
                            queueAnalytics("SUB_CATEGORY", sub.name);
                            navigate(
                              `/books-listing?category=${encodeParam(
                                hoveredCat?.code ?? ""
                              )}&subCategory=${encodeParam(subCatValue)}`
                            )
                          }}
                          className="py-2 px-2 cursor-pointer text-sm lg:text-base text-gray-800 font-gilroyRegular font-semibold tracking-wider rounded-md hover:bg-vintageText hover:bg-opacity-5"
                        >
                          <span className="truncate block" title={sub.name}>
                            {sub.name}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </>
              );
            })() : (
              <p className="text-sm text-gray-400">Hover a category to see subcategories</p>
            )}
          </div>
        </div>

      {/* 🔹 Scoped Scrollbar Hide Style */}
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none; /* IE & Edge */
            scrollbar-width: none; /* Firefox */
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none; /* Chrome, Safari */
          }
        `}
      </style>
    </>
  );
};

export default CategoryDropdown;